<template>
  <div>
    <b-row class="text-center no-gutter">
      <b-col>
        <STabHeader
          class="mb-0"
          :tablist-label="$t('ui.navigation')"
          :tabs="tabs"
          :save-active-tab-in-route="false"
          @change="handleTabClick"
        />
      </b-col>
    </b-row>

    <div class="text-left readers-list">
      <sdui-waterfall-search v-model="readers"
                             :page-size="29"
                             :model="User"
                             class="p-0"
                             :url="url"
                             :custom-fetch-method="fetchReaders"
      >
        <template #quick-actions>
          <div v-if="showFilter || shouldShowExport"
               class="d-flex align-items-center justify-content-between pr-2 pb-4 mb-2"
          >
            <div v-if="showFilter" class="d-flex align-items-center">
              <SRadioButton
                :currently-selected-value="filterMode"
                value-on-selection="all"
                radio-button-type="btn"
                :label-text="$t('news.readers_list.filter.all')"
                name="readersListFilter"
                class="mr-2"
                @change="setFilterMode"
              />
              <SRadioButton
                :currently-selected-value="filterMode"
                value-on-selection="per_family"
                radio-button-type="btn"
                :label-text="$t('news.readers_list.filter.per_family')"
                name="readersListFilter"
                @change="setFilterMode"
              />
            </div>
            <div v-else />
            <SDropDown v-if="shouldShowExport"
                       variant="link"
                       right
                       no-caret
                       :disabled="isExportDisabled"
            >
              <template #button-content>
                <span class="d-flex align-items-center justify-content-center"
                      data-test="export-icon-wrapper"
                      :class="isExportDisabled ? 'text-gray-300' : 'text-primary'"
                >
                  <sdui-icon class="icon-download-o icon-28" />
                  <span class="sr-only">{{ $t('news.readers_list.export.title') }}</span>
                </span>
              </template>
              <SLink :href="meta.csv"
                     download
                     appearance="b-dropdown-item"
                     @click="trackExport('csv')"
              >
                <sdui-icon class="icon-download-o d-inline-block align-middle text-primary" />
                <span class="mx-2" aria-hidden="true">{{ getExportButtonLabel('csv') }}</span>
                <span class="sr-only">{{ getExportButtonSrOnlyLabel('csv') }}</span>
              </SLink>
              <li class="dropdown-divider" aria-hidden="true" />
              <SLink :href="meta.xls"
                     download
                     appearance="b-dropdown-item"
                     @click="trackExport('xlsx')"
              >
                <sdui-icon class="icon-download-o d-inline-block align-middle text-primary" />
                <span class="mx-2" aria-hidden="true">{{ getExportButtonLabel('excel') }}</span>
                <span class="sr-only">{{ getExportButtonSrOnlyLabel('excel') }}</span>
              </SLink>
            </SDropDown>
          </div>
        </template>

        <STabPanel v-if="readers.length"
                   :tab-id="selectedTabId"
                   :active-tab-id="selectedTabId"
        >
          <!-- All mode: flat user list -->
          <template v-if="!isPerFamilyMode">
            <sdui-user-list-item v-for="reader in readers"
                                 :key="reader.id"
                                 class="readers-list-item"
                                 :user="reader"
                                 :prevent-hover="true"
            >
              <template #action>
                <div class="label">
                  {{ reader.meta.is_confirmed ? $t('global.confirmed') : $t('global.open') }}
                </div>
              </template>
            </sdui-user-list-item>
          </template>

          <!-- Per Family mode: family list with parent confirmation status -->
          <template v-else>
            <SListItem v-for="reader in readers"
                       :key="reader.id"
                       :class="getFamilyStatusClass(reader)"
                       icon="icon-channel-parents-56"
                       icon-color="type-parent"
                       :title="getFamilyDisplayName(reader)"
                       :description="getFamilyStatusText(reader)"
            />
          </template>
        </STabPanel>

        <template #noResults>
          <div class="text-center py-5 px-3">
            <template v-if="!is_confirmed">
              <sdui-icon
                class="icon-check-o mb-3"
                variant="success"
                size="xl"
              />
              <p class="font-weight-bold mb-1">{{ $t('news.readers_list.empty_state.open_all_title') }}</p>
              <small class="text-muted d-block">{{ emptyStateDescriptionOpen }}</small>
            </template>
            <template v-else>
              <small class="text-muted d-block">{{ emptyStateTextConfirmed }}</small>
            </template>
          </div>
        </template>
      </sdui-waterfall-search>
    </div>

    <SModalPortalTo v-if="shouldShowNotifyButton">
      <b-button
        v-btn-focus-restore-after-disable
        type="submit"
        size="lg"
        block
        variant="primary"
        :disabled="$wait.waiting('notify')"
        @click="isConfirmNotifyUsersDisplayed = true"
      >
        {{ $t('global.notify') }}
      </b-button>
    </SModalPortalTo>

    <SConfirmModal
      v-model="isConfirmNotifyUsersDisplayed"
      :title="$t('news.readers_list.notify.title')"
      :description="$t('news.readers_list.notify.description')"
      :ok-title="$t('news.readers_list.notify.submit')"
      @ok="notifyAndTrack"
      @hide="isConfirmNotifyUsersDisplayed = false"
    />
  </div>
</template>

<script>
import SduiWaterfallSearch from 'src/components/miscellaneous/ui/common/SduiWaterfallSearch.vue'
import { User } from 'src/store/models/index.js'
import SduiUserListItem from 'src/components/user/SduiUserListItem.vue'
import SListItem from 'src/components/base/SListItem.vue'
import Channels from 'src/api/channels.js'
import axios, { isCancel } from 'src/store/vue-axios/axios.js'
import Misc from 'src/api/misc.js'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import SModalPortalTo from 'src/components/base/SModal/SModalPortalTo.vue'
import STabHeader from 'src/components/base/STabHeader.vue'
import STabPanel from 'src/components/base/STabPanel.vue'
import SRadioButton from 'src/components/base/SRadioButton.vue'

export default {
  name: 'SduiReadersList',
  components: { SModalPortalTo, SduiUserListItem, SListItem, SduiWaterfallSearch, STabHeader, STabPanel, SRadioButton },
  props: {
    itemId: {
      type: [String, Number],
      default: '',
    },

    readBy: {
      type: Number,
      default: 0,
    },

    total: {
      type: Number,
      default: 0,
    },

    meta: {
      type: Object,
      default: () => ({}),
    },

    publishedAt: {
      type: String,
      default: '',
    },

    attachmentCount: {
      type: Number,
      default: 0,
    },

    hasSurvey: {
      type: Boolean,
      default: false,
    },

  },

  data: () => ({
    readers: [],
    is_confirmed: false,
    filterMode: 'all',
    User: User,
    xlsFileSize: 0,
    csvFileSize: 0,
    isConfirmNotifyUsersDisplayed: false,
    confirmedTab: 'confirmed',
    notConfirmedTab: 'not_confirmed',
  }),

  computed: {
    showFilter() {
      return !!this.$me?.school?.properties?.['news.per_family_read_confirmation.enabled'] && !!this.meta?.show_per_family_filter
    },

    isPerFamilyMode() {
      return this.filterMode === 'per_family'
    },

    unreadCount() {
      return this.total - this.readBy
    },

    url() {
      if (this.is_confirmed) {
        return Channels.channelUsersReadNewsByIdUrl(this.itemId, this.is_confirmed, this.isPerFamilyMode)
      }
      return Channels.channelUsersNewsByIdUrl(this.itemId, this.isPerFamilyMode)
    },

    tabs() {
      const openCount = this.total - this.readBy
      const confirmedCount = this.readBy
      return [
        { id: this.notConfirmedTab, text: this.$t('news.readers_list.nav.open', { count: openCount }) },
        { id: this.confirmedTab, text: this.$t('news.readers_list.nav.confirmed', { count: confirmedCount }) },
      ]
    },

    selectedTabId() {
      return this.is_confirmed ? this.confirmedTab : this.notConfirmedTab
    },

    shouldShowNotifyButton() {
      if (this.is_confirmed) return false
      if (this.isPerFamilyMode) return this.unreadCount > 0
      return this.readers.length > 0
    },

    shouldShowExport() {
      return this.is_confirmed && this.meta?.xls && this.meta?.csv
    },

    isExportDisabled() {
      return this.readBy === 0
    },

    emptyStateDescriptionOpen() {
      if (this.isPerFamilyMode) {
        return this.$t('news.readers_list.empty_state.open_per_family_description')
      }
      return this.$t('news.readers_list.empty_state.open_all_description')
    },

    emptyStateTextConfirmed() {
      if (this.isPerFamilyMode) {
        return this.$t('news.readers_list.empty_state.confirmed_per_family')
      }
      return this.$t('news.readers_list.empty_state.confirmed_all')
    },
  },

  methods: {
    async fetchReaders(page, search) {
      this.abortController?.abort()
      this.abortController = new AbortController()

      const separator = this.url.includes('?') ? '&' : '?'
      try {
        return await axios.get(
          `${this.url}${separator}page=${page}&search=${search}`,
          { signal: this.abortController.signal },
        )
      } catch (error) {
        /*
        This could cause memory leaks but fixing this would require
        a larger refactor of the WaterfallSearch. This modal is short-lived
        so we will keep it for now.
         */
        if(isCancel(error)) return new Promise(() => {})
        throw error
      }
    },

    setFilterMode(mode) {
      this.filterMode = mode
      if (mode === 'per_family') {
        this.trackViewByFamily()
      } else {
        this.trackViewAll()
      }
    },

    async initFileSizes() {
      if (!this.meta?.xls || !this.meta?.csv) return
      const [resXls, resCsv] = await Promise.all([Misc.headDownloadableFile(this.meta.xls), Misc.headDownloadableFile(this.meta.csv)])
      this.xlsFileSize = resXls?.headers?.['content-size'] || 0
      this.csvFileSize = resCsv?.headers?.['content-size'] || 0
    },

    notify: waitFor('notify', async function () {
      await Channels.postNewsNotifyById(this.itemId)
    }),

    async notifyAndTrack() {
      await this.notify()
      this.trackSendReminder()
    },

    handleTabClick(tab) {
      this.is_confirmed = tab.id === this.confirmedTab
    },

    getExportButtonLabel(type) {
      if (type === 'csv') {
        return `${this.$t('news.readers_list.export.csv')} (${this.$getSize(this.csvFileSize, false)})`
      }
      return `${this.$t('news.readers_list.export.excel')} (${this.$getSize(this.xlsFileSize, false)})`
    },

    getExportButtonSrOnlyLabel(type) {
      if (type === 'csv') {
        return `${this.$t('news.readers_list.export.export_as', { filetype: 'CSV' })} (${this.$getSize(this.csvFileSize, false)})`
      }
      return `${this.$t('news.readers_list.export.export_as', { filetype: 'XLS' })} (${this.$getSize(this.xlsFileSize, false)})`
    },

    trackExport(fileFormat) {
      clyTracking.trackNews().action.exportReadConfirmations({ fileFormat, ...this.getTrackingSegments() })
    },

    getFamilyDisplayName(reader) {
      return this.$t('news.readers_list.family.parents_of', { name: reader.meta?.displayname })
    },

    getFamilyStatusText(reader) {
      const { total_count: totalCount = 0, confirmed_count: confirmedCount = 0 } = reader.meta?.parent_confirmations || {}

      if (!this.is_confirmed) {
        return `✕ ${this.$t('news.readers_list.family.none_read')}`
      }

      if (confirmedCount >= totalCount) {
        return `✓ ${this.$t('news.readers_list.family.all_read')}`
      }

      return `✓ ${this.$t('news.readers_list.family.partial_read', { current: confirmedCount, total: totalCount })}`
    },

    getFamilyStatusClass(reader) {
      if (!this.is_confirmed) return 'family-status--unread'

      const { total_count: totalCount = 0, confirmed_count: confirmedCount = 0 } = reader.meta?.parent_confirmations || {}

      if (confirmedCount >= totalCount) return 'family-status--all-read'
      return 'family-status--partial-read'
    },

    getTrackingSegments() {
      const publishDate = this.publishedAt ? new Date(this.publishedAt) : null
      const daysSincePublished = publishDate
        ? Math.floor((Date.now() - publishDate.getTime()) / (1000 * 60 * 60 * 24))
        : null
      const weekdayPublished = publishDate
        ? publishDate.toLocaleDateString('en-US', { weekday: 'long' })
        : null

      const families = this.meta?.statistics?.confirmed_families
      const recipientsFamilies = families?.total ?? null
      const unreadFamilies = families ? families.total - families.current : null
      const unreadRatioFamilies = families?.total > 0
        ? Number(((unreadFamilies / families.total) * 100).toFixed(1))
        : null

      return {
        daysSincePublished,
        weekdayPublished,
        recipientsTotal: this.total,
        recipientsFamilies,
        unreadTotal: this.unreadCount,
        unreadFamilies,
        unreadRatioTotal: this.total > 0 ? Number(((this.unreadCount / this.total) * 100).toFixed(1)) : null,
        unreadRatioFamilies,
        fileCount: this.attachmentCount,
        hasSurvey: this.hasSurvey,
      }
    },

    trackOpenModal() {
      clyTracking.trackNews().interaction.viewReadConfirmations(this.getTrackingSegments())
    },

    trackViewByFamily() {
      clyTracking.trackNews().interaction.viewReadConfirmationsByFamily(this.getTrackingSegments())
    },

    trackViewAll() {
      clyTracking.trackNews().interaction.viewReadConfirmationsAll(this.getTrackingSegments())
    },

    trackSendReminder() {
      clyTracking.trackNews().action.sendReminder(this.getTrackingSegments())
    },
  },

  watch: {
    url() {
      this.readers = []
    },
  },

  created() {
    this.abortController = null
  },

  beforeUnmount() {
    this.abortController?.abort()
  },

  async mounted() {
    await this.initFileSizes()
    this.trackOpenModal()
  },
}
</script>

<style scoped lang="scss">
.label {
  text-align: right;
  font-size: pxtorem(13);
  color: $color-gray-300;
  padding-right: 15px;
}

.readers-list {
  :deep(.form-group) {
    padding-top: 0 !important;
  }

  .readers-list-item :deep(.icon-chevron-right) {
    display: none;
  }

  :deep(.family-status--unread .sdui-list-item__description) {
    color: $color-danger-dark !important;
  }

  :deep(.family-status--all-read .sdui-list-item__description) {
    color: $color-success-dark !important;
  }

  :deep(.family-status--partial-read .sdui-list-item__description) {
    color: $color-orange-dark !important;
  }
}
</style>
